import type { ZodError, ZodIssue, ZodTypeAny } from 'zod';
import { fromZodError } from 'zod-validation-error';

import {
  type ErrorConstructor,
  type RestifyHttpErrorOptions,
  assertIsError,
  InternalError,
} from '@fragment/error-utils';
import type { FragmentLogger } from '@fragment/log-utils';
import type { SafeString } from '@fragment/types';
import { SafeStringSchema } from '@fragment/types';

/**
 * An abstraction on top of zod's parse() that allows you to throw custom errors.
 * @param schema: the zod type
 * @param value: the value being parsed
 * @param CustomError: the type of error being thrown.
 * @param options: an optional dictionary of extra metadata to log. similar to `assert()`.
 * @returns
 */
export const parseWithError = <T extends ZodTypeAny>(
  schema: T,
  value: unknown,
  CustomError: ErrorConstructor,
  options: RestifyHttpErrorOptions & { retryable?: boolean } & {
    message?: string;
  } = {}
): ReturnType<T['parse']> => {
  try {
    return schema.parse(value);
  } catch (cause: unknown) {
    assertIsError(cause);

    let zodMessage: string;
    const prefix = options.message ?? 'Failed to parse';
    try {
      zodMessage = fromZodError(cause as ZodError, {
        prefix,
      }).toString();
    } catch (e) {
      zodMessage = `${prefix}: fromZodError failed: ${e}`;
    }

    // Always sanitize the cause for CustomError
    const sanitizedError = new Error((cause as Error).message);
    sanitizedError.name = (cause as Error).name;
    (sanitizedError as any).issues = (cause as ZodError).issues;
    (sanitizedError as any).zodMessage = zodMessage;
    sanitizedError.stack = (cause as Error).stack;

    throw new CustomError(
      {
        cause: sanitizedError,
        ...options,
        info: {
          value,
          ...options.info,
        },
      },
      zodMessage
    );
  }
};

export const safeParse = <T extends ZodTypeAny>(
  schema: T,
  value: unknown,
  logger: FragmentLogger,
  logPayload: boolean = false
): { value: T; success: true } | { value: any; success: false } => {
  try {
    return { value: schema.parse(value), success: true };
  } catch (cause: unknown) {
    assertIsError(cause);
    const msg = fromZodError(cause as ZodError).toString();

    logger.warn(msg, {
      error: cause,
      value: logPayload ? value : '<REDACTED>',
    });
    return { value: value as T, success: false };
  }
};

export const safeGetZodErrors = <T extends ZodTypeAny>(
  schema: T,
  value: unknown,
  pathPrefix?: (string | number)[]
): ZodIssue[] => {
  const res = schema.safeParse(value);
  if (!res.success) {
    return res.error.issues.map(({ path, ...issue }) => ({
      ...issue,
      path: pathPrefix ? [...pathPrefix, ...path] : path,
    }));
  }
  return [];
};

/** Syntactic sugar for parsing a SafeString with a debugging-friendly error message. */
export const parseSafeString = <T extends string>(
  // Make sure that fieldName is a static string. Prevents accidentally passing values in the wrong order.
  fieldName: string extends T ? never : T,
  value: string
): SafeString =>
  parseWithError(SafeStringSchema, value, InternalError, {
    message: `Unsafe ${fieldName} provided: ${value}`,
  });
